import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useFavorites } from '../../context/FavoritesContext';
import { useCart } from '../../context/CartContext';
import contactService from '../../services/contactService';
import pb from '../../services/pocketbase';
import UserProfile from '../../components/UserProfile/UserProfile';
import { FaStar } from 'react-icons/fa';
import './AccountPage.css';

function AccountPage() {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { favorites, addToFavorites, removeFromFavorites, loading: favoritesLoading } = useFavorites();
  const { cartItems, cartTotal, addToCart, updateQuantity, removeFromCart, loading: cartLoading } = useCart();
  
  const [loading, setLoading] = useState(false);
  const [showScrollButton, setShowScrollButton] = useState(false);
  const messagesContainerRef = useRef(null);

  // State for review form
  const [rating, setRating] = useState(0);
  const [comment, setComment] = useState('');
  const [hasSubmittedReview, setHasSubmittedReview] = useState(false);
  const [reviewMessage, setReviewMessage] = useState('');
  const [hover, setHover] = useState(0);

  useEffect(() => {
    if (!user) {
      navigate('/auth');
      return;
    }

    const checkUserReview = async () => {
      try {
        await pb.collection('reviews').getFirstListItem(`user = "${user.id}"`);
        setHasSubmittedReview(true);
      } catch (error) {
        if (error.status !== 404) {
          console.error("Erreur lors de la vérification de l'avis existant:", error);
        }
        setHasSubmittedReview(false);
      }
    };

    checkUserReview();
  }, [user, navigate]);

  const handleReviewSubmit = async (e) => {
    e.preventDefault();
    if (rating === 0 || comment.trim() === '') {
      setReviewMessage('Veuillez donner une note et un commentaire.');
      return;
    }

    try {
      await pb.collection('reviews').create({
        user: user.id,
        rating: rating,
        comment: comment,
        is_approved: false, // Default to not approved
      });
      setHasSubmittedReview(true);
      setReviewMessage('Merci ! Votre avis a été soumis pour approbation.');
    } catch (error) {
      console.error("Erreur lors de la soumission de l'avis:", error);
      setReviewMessage("Une erreur s'est produite. Veuillez réessayer.");
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const getImageUrl = (item) => {
    const imageName = Array.isArray(item.image) ? item.image[0] : item.image;
    if (item && imageName && item.collectionId && item.id) {
      return pb.files.getURL({
        collectionId: item.collectionId,
        id: item.id
      }, imageName, {'thumb': '100x100'});
    }
    return 'https://via.placeholder.com/100'; // Placeholder image
  };

  if (!user) {
    return null; // Affiche une page vide ou un spinner pendant la redirection
  }

  return (
    <div className="account-page">
      <div className="account-container">
        <h2>Mon Compte</h2>
        <div className="account-section profile-section">
          <UserProfile />
        </div>

        <div className="account-section favorites-section">
          <h3>💖 Mes Favoris</h3>
          {favoritesLoading ? (
            <div className="loading-state">
              <div className="loading-spinner"></div>
              <p>Chargement des favoris...</p>
            </div>
          ) : favorites.length > 0 ? (
            <>
              <div className="section-stats">
                <span className="item-count">{favorites.length} produit{favorites.length > 1 ? 's' : ''}</span>
                <span className="total-value">Valeur totale: {favorites.reduce((total, item) => total + (item.price || 0), 0).toFixed(2)} €</span>
              </div>
              <div className="favorites-grid">
                {favorites.map(product => (
                  <div key={product.id} className="favorite-item">
                    <div 
                      className="favorite-image-wrapper"
                      onClick={() => navigate(`/catalogue/produit/${product.slug}`)}
                      style={{ cursor: 'pointer' }}
                    >
                      <img src={getImageUrl(product)} alt={product.name} />
                      <div className="favorite-overlay">
                        <button className="quick-view-btn">👁️ Voir</button>
                      </div>
                    </div>
                    <div className="favorite-item-details">
                      <h4 
                        onClick={() => navigate(`/catalogue/produit/${product.slug}`)}
                        style={{ cursor: 'pointer' }}
                      >
                        {product.name}
                      </h4>
                      <p className="favorite-price">{(product.price || 0).toFixed(2)} €</p>
                      <div className="favorite-actions">
                        <button 
                          className="add-to-cart-btn"
                          onClick={() => addToCart(product)}
                        >
                          🛒 Ajouter
                        </button>
                        <button 
                          className="remove-favorite-btn"
                          onClick={() => removeFromFavorites(product.id)}
                        >
                          ❌
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </>
          ) : (
            <div className="empty-state">
              <div className="empty-icon">💔</div>
              <p>Vous n'avez pas encore de favoris.</p>
              <p className="empty-subtitle">Explorez nos produits et ajoutez vos coups de cœur !</p>
            </div>
          )}
        </div>

        <div className="account-section cart-section">
          <h3>🛒 Mon Panier</h3>
          {cartLoading ? (
            <div className="loading-state">
              <div className="loading-spinner"></div>
              <p>Chargement du panier...</p>
            </div>
          ) : cartItems.length > 0 ? (
            <>
              <div className="section-stats">
                <span className="item-count">{cartItems.length} article{cartItems.length > 1 ? 's' : ''}</span>
                <span className="total-items">Quantité totale: {cartItems.reduce((total, item) => total + item.quantity, 0)}</span>
              </div>
              <div className="cart-items-list">
                {cartItems.map(item => (
                  <div key={item.id} className="cart-item">
                    <div 
                      className="cart-item-image"
                      onClick={() => navigate(`/catalogue/produit/${item.slug}`)}
                      style={{ cursor: 'pointer' }}
                    >
                      <img src={getImageUrl(item)} alt={item.name} />
                      <div className="quantity-badge">{item.quantity}</div>
                    </div>
                    <div className="cart-item-details">
                      <h4 
                        onClick={() => navigate(`/catalogue/produit/${item.slug}`)}
                        style={{ cursor: 'pointer' }}
                      >
                        {item.name}
                      </h4>
                      <div className="cart-item-pricing">
                        <span className="unit-price">{(item.price || 0).toFixed(2)} € × {item.quantity}</span>
                        <span className="total-price">{((item.price || 0) * item.quantity).toFixed(2)} €</span>
                      </div>
                      <div className="cart-item-actions">
                        <div className="quantity-controls">
                          <button className="qty-btn" onClick={() => updateQuantity(item.id, -1)}>−</button>
                          <span className="qty-display">{item.quantity}</span>
                          <button className="qty-btn" onClick={() => updateQuantity(item.id, 1)}>+</button>
                        </div>
                        <button className="remove-item-btn" onClick={() => removeFromCart(item.id)}>🗑️</button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              <div className="cart-summary">
                <div className="cart-total">
                  <span className="total-label">Total du panier</span>
                  <span className="total-amount">{cartTotal.toFixed(2)} €</span>
                </div>
                <button 
                  className="checkout-btn"
                  onClick={() => navigate('/contact')}
                >
                  💬 Contacter pour commander
                </button>
              </div>
            </>
          ) : (
            <div className="empty-state">
              <div className="empty-icon">🛒</div>
              <p>Votre panier est vide.</p>
              <p className="empty-subtitle">Découvrez nos créations uniques et commencez vos achats !</p>
            </div>
          )}
        </div>

        <div className="account-section review-section">
          <h3>Laissez un avis</h3>
          {hasSubmittedReview ? (
            <p>Merci, votre avis a bien été pris en compte.</p>
          ) : (
            <form onSubmit={handleReviewSubmit} className="review-form">
              <div className="star-rating">
                {[...Array(5)].map((star, index) => {
                  const ratingValue = index + 1;
                  return (
                    <label key={index}>
                      <input
                        type="radio"
                        name="rating"
                        value={ratingValue}
                        onClick={() => setRating(ratingValue)}
                        style={{ display: 'none' }}
                      />
                      <FaStar
                        className="star"
                        color={ratingValue <= (hover || rating) ? "#ffc107" : "#e4e5e9"}
                        size={25}
                        onMouseEnter={() => setHover(ratingValue)}
                        onMouseLeave={() => setHover(0)}
                      />
                    </label>
                  );
                })}
              </div>
              <textarea
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                placeholder="Votre commentaire..."
                rows="4"
              ></textarea>
              <button type="submit">Envoyer mon avis</button>
              {reviewMessage && <p className="review-message">{reviewMessage}</p>}
            </form>
          )}
        </div>

        <div className="account-section contact-section">
          <h3>💌 Contacter Magali</h3>
          <p className="section-intro">Une question sur un bijou ou une commande ? Envoyez-moi un message via ce formulaire.</p>
          
          <div className="contact-form-container">
            <form onSubmit={async (e) => {
              e.preventDefault();
              const formData = new FormData(e.target);
              const data = {
                name: user?.name || user?.username || 'Utilisateur',
                email: user?.email,
                message: formData.get('message'),
                user: user?.id // Lien optionnel avec l'utilisateur
              };
              
              if (!data.message.trim()) return;

              try {
                setLoading(true);
                await contactService.sendMessage(data);
                alert('Message envoyé avec succès ! Magali vous répondra par email ou via votre compte.');
                e.target.reset();
              } catch (err) {
                alert('Erreur lors de l\'envoi du message.');
              } finally {
                setLoading(false);
              }
            }} className="account-contact-form">
              <div className="form-group">
                <label>Votre Message</label>
                <textarea 
                  name="message" 
                  placeholder="Posez votre question ici..."
                  rows="5"
                  required
                ></textarea>
              </div>
              <button type="submit" className="submit-btn" disabled={loading}>
                {loading ? 'Envoi en cours...' : 'Envoyer le message'}
              </button>
            </form>
          </div>
        </div>

        <button onClick={handleLogout} className="logout-button">Se déconnecter</button>
      </div>
    </div>
  );
}

export default AccountPage;
